import { useEffect, useSyncExternalStore } from "react";
import { AUTH_API_BASE_URL, AUTH_ME_PATH } from "@config";
import { fetchAuth } from "./api";

export type MeData = {
  roles: string[];
  club_id?: string;
  user_id?: string;
  account_kind?: string;
  player_id?: string;
  app?: string;
};

type MeState = {
  me: MeData | null;
  loading: boolean;
  err: string;
};

const RETRY_DELAYS_MS = [0, 400, 1200];

let state: MeState = { me: null, loading: false, err: "" };
let fetchPromise: Promise<void> | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function setState(patch: Partial<MeState>) {
  state = { ...state, ...patch };
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot() {
  return state;
}

function getServerSnapshot() {
  return state;
}

async function loadMe(force = false): Promise<void> {
  if (fetchPromise && !force) {
    return fetchPromise;
  }

  if (state.me && !force) {
    return;
  }

  fetchPromise = (async () => {
    setState({ loading: true, err: "" });

    let lastError = "";

    for (let attempt = 0; attempt < RETRY_DELAYS_MS.length; attempt += 1) {
      if (RETRY_DELAYS_MS[attempt] > 0) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, RETRY_DELAYS_MS[attempt]);
        });
      }

      try {
        const res = await fetchAuth(`${AUTH_API_BASE_URL}${AUTH_ME_PATH}`);
        const data = await res.json();
        if (!res.ok) {
          const message =
            typeof data.error === "string" ? data.error : data.error?.message || "me_failed";
          throw new Error(message);
        }

        setState({
          me: {
            roles: data.roles || [],
            club_id: data.club_id,
            user_id: data.user_id,
            account_kind: data.account_kind,
            player_id: data.player_id,
            app: data.app,
          },
          loading: false,
          err: "",
        });
        return;
      } catch (error: unknown) {
        lastError = String((error as Error)?.message || error);
      }
    }

    setState({ loading: false, err: lastError || "me_failed" });
  })().finally(() => {
    fetchPromise = null;
  });

  return fetchPromise;
}

/** Clears cached profile and refetches (call after login/logout). */
export function invalidateMeCache(options?: { refetch?: boolean }) {
  state = { me: null, loading: false, err: "" };
  fetchPromise = null;
  emit();

  if (options?.refetch !== false) {
    void loadMe(true);
  }
}

export function useMe() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  useEffect(() => {
    void loadMe();
  }, []);

  return { me: snapshot.me, loading: snapshot.loading, err: snapshot.err };
}
